import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { IconCheck, IconClose } from '../components/icons';

/**
 * Lightweight toast notification system. Singleton-ish: wrap the app once
 * with <ToastProvider>, then call useToast() anywhere to push a toast.
 *
 * - kind: success / error / info
 * - auto-dismiss after 4s
 * - max 5 stacked, oldest evicted
 * - fixed bottom-right, slides in from the right
 */

type ToastKind = 'success' | 'error' | 'info';
interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  detail?: string;
}

interface ToastContextValue {
  push: (t: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_STACK = 5;
const AUTO_DISMISS_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
    setToasts(prev => {
      const next = [...prev, { ...t, id }];
      return next.length > MAX_STACK ? next.slice(next.length - MAX_STACK) : next;
    });
    setTimeout(() => {
      setToasts(prev => prev.filter(x => x.id !== id));
    }, AUTO_DISMISS_MS);
  }, []);

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={id => setToasts(prev => prev.filter(t => t.id !== id))} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 md:left-auto md:right-4 md:translate-x-0 z-[60] flex flex-col gap-2 items-stretch md:items-end pointer-events-none px-3 md:px-0 w-full md:w-auto max-w-[420px]"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}
    >
      {toasts.map(t => (
        <div
          key={t.id}
          className="pointer-events-auto bg-white border border-line shadow-pop rounded-lg px-4 py-3 flex items-start gap-3 min-w-[280px] max-w-[420px] animate-[slideIn_.2s_ease]"
          style={{ animation: 'slideIn 200ms cubic-bezier(.2,.8,.2,1)' }}
        >
          <ToastIcon kind={t.kind} />
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-medium text-ink leading-tight">{t.message}</div>
            {t.detail && <div className="text-[12px] text-ink-muted mt-0.5 truncate">{t.detail}</div>}
          </div>
          <button onClick={() => onDismiss(t.id)} className="text-ink-faint hover:text-ink shrink-0">
            <IconClose className="w-4 h-4" />
          </button>
        </div>
      ))}
      <style>{`@keyframes slideIn { from { opacity: 0; transform: translateX(8px) } to { opacity: 1; transform: translateX(0) } }`}</style>
    </div>
  );
}

function ToastIcon({ kind }: { kind: ToastKind }) {
  if (kind === 'success') {
    return (
      <span className="w-5 h-5 rounded-full bg-funds text-white grid place-items-center shrink-0 mt-0.5">
        <IconCheck className="w-3 h-3" />
      </span>
    );
  }
  if (kind === 'error') {
    return (
      <span className="w-5 h-5 rounded-full bg-red-500 text-white grid place-items-center shrink-0 mt-0.5 text-[11px] font-bold">!</span>
    );
  }
  return (
    <span className="w-5 h-5 rounded-full bg-navy text-white grid place-items-center shrink-0 mt-0.5 text-[11px] font-bold">i</span>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback: no-op when used outside provider so the app doesn't crash.
    // Useful in tests / Storybook.
    return {
      success: (_m: string, _d?: string) => {},
      error:   (_m: string, _d?: string) => {},
      info:    (_m: string, _d?: string) => {},
    };
  }
  return {
    success: (message: string, detail?: string) => ctx.push({ kind: 'success', message, detail }),
    error:   (message: string, detail?: string) => ctx.push({ kind: 'error',   message, detail }),
    info:    (message: string, detail?: string) => ctx.push({ kind: 'info',    message, detail }),
  };
}

/* Helper: register a global keyboard shortcut, returns the cleanup. */
export function useKeyboardShortcut(key: string, handler: (e: KeyboardEvent) => void, opts?: { meta?: boolean; ctrl?: boolean }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== key) return;
      if (opts?.meta && !e.metaKey) return;
      if (opts?.ctrl && !e.ctrlKey) return;
      // Match either meta OR ctrl when both flags are set (cross-platform).
      if (opts?.meta && opts?.ctrl && !(e.metaKey || e.ctrlKey)) return;
      handler(e);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [key, handler, opts]);
}
